/**
 * types.ts — kontrakt widżet/układ pulpitu tenanta (szew rozszerzalności).
 *
 * `DashboardLayoutDoc` to jeden blob JSON zapisywany w całości do bazy tenanta
 * (jak `listConfig` w Kreatorze Listy). Widżety to dyskryminowany union po
 * `type` — dołożenie odłożonego typu (iter. 2+) jest czysto addytywne: nowy
 * wariant unii + wpis w rejestrze, zero zmian w siatce/ramie.
 *
 * Kontrakt MUSI być zgodny z walidatorem zod backendu
 * (`apps/api/lib/validators/dashboard.ts`) — typy widżetów, breakpointy, kształt
 * geometrii. Backend waliduje `config` z `.passthrough()`, więc dołożenie pola
 * configu nie wymaga migracji ani zmiany backendu.
 */

import { type WidgetVisualType } from '@/lib/widgets/types';

// Typy metryk/encji — lustro rejestru backendu (`lib/dashboard/metrics.ts`).
export const KPI_METRICS = [
  'contractors',
  'companies',
  'employees',
  'products',
  'purchase_invoices',
  // Metryki „biznesowe" z pulpitu wg Figmy (kwota / liczba / godziny); jednostkę zwraca backend.
  'sales_revenue',
  'documents_total',
  'avg_approval_hours',
  'active_users',
] as const;
export type KpiMetric = (typeof KPI_METRICS)[number];

// Encje per-firma — używane przez `recent` i `chart` (liczą/listują w kontekście
// aktywnej firmy). `companies` celowo poza tym zbiorem (patrz backend metrics).
export const COMPANY_ENTITIES = [
  'contractors',
  'employees',
  'products',
  'purchase_invoices',
] as const;
export type CompanyEntity = (typeof COMPANY_ENTITIES)[number];

// Encje wykresów i rozkładów: jak `COMPANY_ENTITIES` + faktury sprzedaży (lustro
// `DASHBOARD_ENTITIES` backendu). „Ostatnie rekordy" nadal używają węższej listy.
export const DASHBOARD_ENTITIES = [...COMPANY_ENTITIES, 'sale_invoices'] as const;
export type DashboardEntity = (typeof DASHBOARD_ENTITIES)[number];

/** Encje, dla których wykres może liczyć kwotę brutto zamiast liczby rekordów. */
export const AMOUNT_ENTITIES: readonly DashboardEntity[] = ['sale_invoices', 'purchase_invoices'];

/** Jednostka wartości KPI zwracana przez backend. */
export type KpiUnit = 'count' | 'amount' | 'hours';

export const DASHBOARD_PERIODS = ['today', 'week', 'month', 'year', 'all'] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];

export const CHART_BUCKETS = ['day', 'week', 'month'] as const;
export type ChartBucket = (typeof CHART_BUCKETS)[number];

export const WIDGET_TYPES = [
  'kpi',
  'pending_approvals',
  'recent_records',
  'activity_feed',
  'chart',
  // Wykres kołowy: rozkład metryki po firmach użytkownika.
  'breakdown',
  // Słupki grupowe „Nowe vs Zakończone" (dokumenty w obiegu).
  'flow',
  // Paski wydajności procesów + wskaźniki kołowe.
  'process_performance',
  // Ostatnie transakcje (faktury sprzedaży/zakupu ze statusem).
  'transactions',
  // Powiadomienia wyliczane na żywo (aktualizacje, terminy, błędy integracji).
  'alerts',
  // Karta profilu zalogowanego użytkownika.
  'profile',
  'quick_actions',
  'note',
  'countdown',
  // Widżet danych z kreatora — renderuje zapisaną definicję po `config.definitionId`.
  'custom',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export const BREAKPOINTS = ['lg', 'md', 'sm', 'xs'] as const;
export type Breakpoint = (typeof BREAKPOINTS)[number];

// --- config per typ widżetu ---

interface BaseWidgetConfig {
  /** Tytuł nadpisany przez usera; brak → domyślny z rejestru (i18n). */
  title?: string;
  /** Nazwa ikony (klucz z palety ikon); brak → domyślna z rejestru. */
  icon?: string;
}

export const KPI_VARIANTS = ['card', 'tile'] as const;
export type KpiVariant = (typeof KPI_VARIANTS)[number];

export interface KpiConfig extends BaseWidgetConfig {
  metric: KpiMetric;
  /** `card` (domyślnie) = karta statystyki z nagłówkiem; `tile` = kompaktowy kafelek ikona + wartość. */
  variant?: KpiVariant;
}

export type PendingApprovalsConfig = BaseWidgetConfig;

export interface RecentRecordsConfig extends BaseWidgetConfig {
  entity: CompanyEntity;
  limit?: number;
}

export interface ActivityFeedConfig extends BaseWidgetConfig {
  limit?: number;
}

export const CHART_VARIANTS = ['area', 'bar', 'line'] as const;
export type ChartVariant = (typeof CHART_VARIANTS)[number];

export interface ChartConfig extends BaseWidgetConfig {
  entity: DashboardEntity;
  /** Grupowanie; brak → dobierane do okresu (rok = miesiące, krótsze okresy = dni). */
  bucket?: ChartBucket;
  /** Wygląd wykresu; brak → `area` (zgodność z kafelkami zapisanymi przed tą opcją). */
  variant?: ChartVariant;
  /** `amount` = suma brutto (tylko faktury); brak → `count` (liczba rekordów). */
  measure?: 'count' | 'amount';
  /** Linia porównawcza z poprzednim okresem (np. rok do roku); brak → wyłączona. */
  compare?: boolean;
}

/** Wymiar rozkładu: po firmach (domyślnie) albo po statusie dokumentu (tylko faktury). */
export type BreakdownDimension = 'company' | 'status';

export interface BreakdownConfig extends BaseWidgetConfig {
  /** Metryka rozkładana po firmach lub statusach (te same encje co wykres). */
  metric: DashboardEntity;
  dimension?: BreakdownDimension;
}

export interface FlowConfig extends BaseWidgetConfig {
  bucket?: ChartBucket;
}

export type ProcessPerformanceConfig = BaseWidgetConfig;

export interface TransactionsConfig extends BaseWidgetConfig {
  limit?: number;
}

export type AlertsConfig = BaseWidgetConfig;

export type ProfileConfig = BaseWidgetConfig;

export interface QuickAction {
  label: string;
  /** Ścieżka wewnętrzna (react-router), np. `/contractors/new`. */
  to: string;
  icon?: string;
  /** Krótki opis pod tytułem karty akcji. */
  description?: string;
}

export interface QuickActionsConfig extends BaseWidgetConfig {
  actions: QuickAction[];
}

export interface NoteConfig extends BaseWidgetConfig {
  /** Treść rich-text (HTML z TipTap); limit długości po stronie backendu. */
  html?: string;
}

export interface CountdownConfig extends BaseWidgetConfig {
  /** Data docelowa odliczania w ISO 8601. */
  targetIso?: string;
}

export interface CustomConfig extends BaseWidgetConfig {
  /** UUID zapisanej definicji (`widget_definitions.id`) — źródło prawdy dla obliczenia. */
  definitionId: string;
  /**
   * Snapshot typu wizualnego z chwili osadzenia — fallback na czas przed pierwszym fetchem
   * (i dla kafli sprzed tej zmiany). Odpowiedź endpointu danych (`WidgetDataApiResponse`) niesie
   * ŻYWY `visualType`, który kafel preferuje w renderze (patrz `CustomWidget.tsx`) — ten snapshot
   * nie jest już jedynym źródłem prawdy. MAKRO-dispatch (KPI vs wykres vs tabela) idzie po
   * `value.kind` (zawsze świeże z compute), niezależnie od tego pola.
   */
  visualType?: WidgetVisualType;
}

// Slot na odłożone stylowanie (kolory/glass/czcionki) — iter. 1 nie czyta/zapisuje.
export type WidgetAppearance = Record<string, unknown>;

interface WidgetBase<TType extends WidgetType, TConfig> {
  /** Stabilny identyfikator instancji widżetu (== `i` w geometrii RGL). */
  id: string;
  type: TType;
  config: TConfig;
  appearance?: WidgetAppearance;
}

export type DashboardWidget =
  | WidgetBase<'kpi', KpiConfig>
  | WidgetBase<'pending_approvals', PendingApprovalsConfig>
  | WidgetBase<'recent_records', RecentRecordsConfig>
  | WidgetBase<'activity_feed', ActivityFeedConfig>
  | WidgetBase<'chart', ChartConfig>
  | WidgetBase<'breakdown', BreakdownConfig>
  | WidgetBase<'flow', FlowConfig>
  | WidgetBase<'process_performance', ProcessPerformanceConfig>
  | WidgetBase<'transactions', TransactionsConfig>
  | WidgetBase<'alerts', AlertsConfig>
  | WidgetBase<'profile', ProfileConfig>
  | WidgetBase<'quick_actions', QuickActionsConfig>
  | WidgetBase<'note', NoteConfig>
  | WidgetBase<'countdown', CountdownConfig>
  | WidgetBase<'custom', CustomConfig>;

// Geometria pojedynczego kafelka w siatce RGL (per breakpoint).
export interface WidgetLayoutItem {
  /** Id instancji widżetu (łączy geometrię z `DashboardWidget.id`). */
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

export interface DashboardLayoutDoc {
  version: 1;
  /** Miejsce na wiele nazwanych układów (iter. 2+) — iter. 1 nieużywane. */
  name?: string;
  widgets: DashboardWidget[];
  layouts: Record<Breakpoint, WidgetLayoutItem[]>;
}

/**
 * Wspólny kontrakt propsów komponentu kafelka (lookup w rejestrze). Komponent
 * dostaje całą instancję widżetu i zawęża po `widget.type` wewnętrznie.
 * `period` przychodzi tylko dla widżetów wspierających filtr okresu.
 */
export interface WidgetRenderProps {
  widget: DashboardWidget;
  period?: DashboardPeriod;
  editMode?: boolean;
}
